import { ApolloProvider } from '@apollo/client';
import { Button, ConfigProvider, Dropdown } from 'antd';
import { observer } from 'mobx-react-lite';
import {
  Link,
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';
import { AuthDialog } from './components/AuthDialog';
import { NavList } from './components/NavList';
import { RequireAuth } from './components/PermissionLink';
import { RegisterDialog } from './components/RegisterDialog';
import { UserAvatar } from './components/UserAvatar';
import { useMenuItems } from './hooks/useMenuItems';
import { useApolloClient } from './lib/apollo';
import { showLoginDialog } from './lib/apollo/common';
import { setupCrashTracking } from './lib/sentry';
import { HomePage } from './pages/Home';
import ReferralCodesPage from './pages/admin/referral-codes';
import { ApiKeyPage } from './pages/api-key';
import { BillingPage } from './pages/billing';
import { DebugPage } from './pages/debug';
import { LiveChatPage } from './pages/live-chat';
import { MenuPage } from './pages/menu';
import { PairingPage } from './pages/pairing';
import { EditProfilePage } from './pages/profile/edit';
import { UsagePage } from './pages/usage';
import { WineListPage } from './pages/wine-list';
import { AuthProvider, useAuth } from './stores/AuthContext';

setupCrashTracking();

const AuthButtons = observer(() => {
  const { user, isLoading } = useAuth();
  const { items, showRegisterDialog, setShowRegisterDialog } = useMenuItems();

  if (isLoading) {
    return <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />;
  }

  if (user) {
    return <UserAvatar />;
  }

  return (
    <div className="flex gap-2">
      <Dropdown menu={{ items }} placement="bottomRight">
        <Button type="default" onClick={() => showLoginDialog()}>
          Login
        </Button>
      </Dropdown>
      <RegisterDialog
        open={showRegisterDialog}
        onOpenChange={setShowRegisterDialog}
      />
    </div>
  );
});

const AppContent = () => {
  return (
    <Router>
      <div className="flex min-h-screen min-w-[1024px] flex-col h-screen overflow-hidden">
        <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 w-full">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-xl font-bold">
                Sommly
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <NavList />
              <div className="ml-4 pl-4 border-l">
                <AuthButtons />
              </div>
            </div>
          </div>
        </nav>

        <div className="flex-1 flex justify-center overflow-auto">
          <div className="w-full max-w-[1200px] flex-1 h-full">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/pairing" element={<PairingPage />} />
              <Route path="/live-chat" element={<LiveChatPage />} />
              <Route path="/debug" element={<DebugPage />} />
              <Route
                path="/admin/referral-codes"
                element={
                  <RequireAuth
                    permissions={['admin']}
                    fallback={<Navigate to="/unauthorized" />}
                  >
                    <ReferralCodesPage />
                  </RequireAuth>
                }
              />
              <Route path="/profile/edit" element={<EditProfilePage />} />
              <Route path="/api-key" element={<ApiKeyPage />} />
              <Route path="/menu" element={<MenuPage />} />
              <Route path="/wine-list" element={<WineListPage />} />
              <Route path="/usage" element={<UsagePage />} />
              <Route path="/billing" element={<BillingPage />} />
            </Routes>
          </div>
        </div>
      </div>
      <AuthDialog />
    </Router>
  );
};

function App() {
  const apolloClient = useApolloClient();

  return (
    <ConfigProvider>
      <ApolloProvider client={apolloClient}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ApolloProvider>
    </ConfigProvider>
  );
}

export default App;
